import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { CompressFileOptions } from '@paintscout/util/s3'
import { compressFile, uploadFile } from '@paintscout/util/s3'
import classnames from 'classnames'
import type { QuoteFile } from 'paintscout'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { DropzoneProps } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'

import { useApolloClient } from '@apollo/react-hooks'
import Typography from '@material-ui/core/Typography'
import { getFeature } from '@paintscout/util/builder'
import type { StyleClasses } from '@paintscout/ui/styles'
import Spinner from '@paintscout/ui/components/Spinner'
import { hiddenFileInputStyle } from '@paintscout/ui/components/Dropzone'
import Grid from '@paintscout/ui/components/Grid'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import { useCompanyId } from '../../hooks/useCompanyId'

export interface FileDropAreaProps extends Omit<CompressFileOptions, 'file'>, Omit<DropzoneProps, 'children'> {
  onUpload: (files: QuoteFile[]) => void
  onUploadStart?: () => void
  onPartialUpload?: ({ totalFiles, index }: { totalFiles: number; index: number }) => void
  onPartialUploadError?: ({ totalFiles, index }: { totalFiles: number; index: number }) => void
  label?: string
  docId?: string
  /**
   * LaunchDarkly `s3MediaUpload` flag value. When true, PDFs/videos dropped here
   * upload to S3 instead of Cloudinary. Passed in by the consumer (which can read
   * `useFlags()`) since ui-business has no flags provider of its own; defaults to
   * off so behaviour is unchanged when omitted.
   */
  s3MediaUploadEnabled?: boolean
  children: React.ReactNode
  maxHeight?: CompressFileOptions['maxHeight']
  maxWidth?: CompressFileOptions['maxWidth']
  quality?: CompressFileOptions['quality']
  classes?: StyleClasses<typeof useStyles>
}

const useStyles = makeStyles(
  (_theme: Theme) => {
    return {
      root: {
        position: 'relative'
      },
      draggingFileOverlay: {
        display: 'none',

        border: '2px dashed #aaa',
        content: "' '",
        height: '100%',

        alignItems: 'center',
        justifyContent: 'center',

        width: '100%',
        position: 'absolute',
        borderRadius: '3px',
        zIndex: 50,
        background: '#ddd'
      },
      draggingFile: {
        position: 'relative',
        '& $draggingFileOverlay': {
          display: 'flex'
        },
        // the below is so FilesGrid deleteIcons dont overflow
        '& svg': {
          display: 'none'
        }
      },
      loadingFile: {
        position: 'relative',
        '& $draggingFileOverlay': {
          display: 'flex'
        },
        // the below is so FilesGrid deleteIcons dont overflow
        '& svg': {
          display: 'none'
        }
      }
    }
  },
  { name: 'FileDropArea' }
)

export default function FileDropArea({
  onUpload,
  onUploadStart,
  onPartialUpload,
  onPartialUploadError,
  children,
  docId,
  label,
  maxHeight,
  maxWidth,
  s3MediaUploadEnabled,
  ...otherProps
}: FileDropAreaProps) {
  const apolloClient = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [filesComplete, setFilesComplete] = useState(0)
  const [filesErrored, setFilesErrored] = useState(0)
  const [totalFiles, setTotalFiles] = useState(0)
  const { options } = useClientOptions()
  const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })

  // #22526: resolves to `clientId` when no `UserProvider` is mounted. Shared with `useFileDropArea`,
  // which is the newer parallel implementation of this same upload path.
  const companyId = useCompanyId()

  /**
   * The caller's callbacks, kept current without putting them in `onDrop`'s dep array. Callers pass
   * inline arrow functions, so listing them would re-create `onDrop` on every parent render.
   */
  const callbacks = useRef({ onUpload, onUploadStart, onPartialUpload, onPartialUploadError })
  useEffect(() => {
    callbacks.current = { onUpload, onUploadStart, onPartialUpload, onPartialUploadError }
  }, [onUpload, onUploadStart, onPartialUpload, onPartialUploadError])

  const onDrop = useCallback(
    async (uploadFiles: File[]) => {
      if (callbacks.current.onUploadStart) {
        callbacks.current.onUploadStart()
      }

      setLoading(true)
      setTotalFiles(uploadFiles.length)

      const allUploadRes = []
      for (let i = 0; i < uploadFiles.length; i++) {
        try {
          const file = uploadFiles[i]
          const compressedFile = await compressFile({
            file,
            maxHeight: maxHeight ?? 5000,
            maxWidth: maxWidth ?? 1920
          })

          const uploadedFile = await uploadFile({
            file: compressedFile,
            apolloClient,
            uploadOptions: {
              companyId,
              docId,
              s3UploadFeatureEnabled,
              s3MediaUploadEnabled
            }
          })

          if (callbacks.current.onPartialUpload) {
            callbacks.current.onPartialUpload({ totalFiles, index: i })
          }
          setFilesComplete(filesComplete + 1)

          allUploadRes.push(uploadedFile)
        } catch (err) {
          if (callbacks.current.onPartialUploadError) {
            callbacks.current.onPartialUploadError({ totalFiles, index: i })
          }
          setFilesErrored(filesErrored + 1)
          allUploadRes.push(err)
        }
      }

      setLoading(false)

      callbacks.current.onUpload(allUploadRes)
      // The empty dep array pinned the first render's values, so `companyId` stayed `undefined` even after
      // the provider resolved it. Only the values that decide WHERE a file lands are deps. The
      // `filesComplete`/`filesErrored` counters are deliberately NOT deps: they are read inside a loop that
      // has already started, so a fresh closure cannot un-stale them.
    },
    [apolloClient, companyId, docId, s3UploadFeatureEnabled, s3MediaUploadEnabled, maxHeight, maxWidth]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    ...otherProps
  })

  const classes = useStyles(otherProps)

  return (
    <div
      className={classnames(
        classes.root,
        isDragActive ? classes.draggingFile : null,
        loading ? classes.loadingFile : null
      )}
      {...getRootProps()}
    >
      <input {...getInputProps({ style: hiddenFileInputStyle })} />
      <div className={classes.draggingFileOverlay}>
        <Grid container alignItems="center" justifyContent="center">
          {loading && (
            <Grid item container spacing={2} alignItems="center" justifyContent="center">
              <Grid item>
                <Spinner size={10} />
              </Grid>
              <Grid item>
                <Typography variant="overline">Uploading {`${filesComplete}/${totalFiles}`}...</Typography>
              </Grid>
            </Grid>
          )}
          {!loading && (
            <Grid item>
              <Typography variant="overline">{label ?? 'Drop Files to Add'}</Typography>
            </Grid>
          )}
        </Grid>
      </div>
      {children}
    </div>
  )
}
